

import { useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/common/services/httpClient';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { Button, Card, Col, Row, Switch, message, Spin } from 'antd';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { useAppMutation } from '@/common/hooks/useAppMutation';
import type { ApiResponse, Permission } from '../../types/managementTypes';
import { Modal } from 'antd';



const fetchRolePermissions = async (roleId: string): Promise<Permission[]> => {
    const response = await get<ApiResponse>(`${API_ENDPOINTS.MANAGEMENT.GET_ROLE_ACTIONS}/${roleId}`);
    return response.data;
};

const saveRolePermissions = async (data: { roleId: string; roleName: string; permissions: Permission[] }) => {
    const roleActionsRequest = data.permissions.flatMap(p =>
        p.actions.map(a => ({
            actionName: a.actionName,
            controllerName: p.controllerName,
            isChecked: a.isChecked,
        }))
    );
    const payload = {
        roleActionsRequest,
        roleId: parseInt(data.roleId, 10),
        roleName: data.roleName,
    };
    await post(API_ENDPOINTS.MANAGEMENT.SAVE_ROLE_ACTIONS, payload);
};

const syncRoleActions = async (data: unknown) => {
    await post(API_ENDPOINTS.MANAGEMENT.SYNC_ACTIONS, data);
};

function RolePermissions() {
    const navigate = useNavigate();
    const { state } = useRouter().state.location;
    const roleId = (state as { id?: string })?.id || '';
    const roleName = (state as { name?: string })?.name || '';
    const queryClient = useQueryClient();

    const [permissions, setPermissions] = useState<Permission[]>([]);

    const { data, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.MANAGEMENT.ROLE_ACTIONS, roleId],
        () => fetchRolePermissions(roleId)
    );

    const isAllSelected = () => {
        return permissions.every(p =>
            p.actions.every(a => a.isChecked)
        );
    };

    useEffect(() => {
        if (data) {
            setPermissions(data);
        }
    }, [data]);

    const mutation = useAppMutation(
        saveRolePermissions,
        {
            onSuccess: () => {
                message.success('Permissions saved successfully!');
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MANAGEMENT.ROLE_ACTIONS, roleId] });
            },
            onError: () => {
                message.error('Failed to save permissions.');
            },
        }
    );

    const mutationSync = useAppMutation(
        syncRoleActions,
        {
            onSuccess: () => {
                message.success('action sync successfully!');
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MANAGEMENT.ROLE_ACTIONS, roleId] });
            },
            onError: () => {
                message.error('Failed to save permissions.');
            },
        }
    );

    const handleSwitchChange = (controllerName: string, actionName: string, checked: boolean) => {
        setPermissions(prevPermissions =>
            prevPermissions.map(p =>
                p.controllerName === controllerName
                    ? {
                        ...p,
                        actions: p.actions.map(a =>
                            a.actionName === actionName ? { ...a, isChecked: checked } : a
                        ),
                    }
                    : p
            )
        );
    };

    const handleToggleAll = (checked: boolean) => {
        Modal.confirm({
            title: checked ? 'Select All Permissions' : 'Unselect All Permissions',
            content: `Are you sure you want to ${checked ? 'select' : 'unselect'} all permissions?`,
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => {
                setPermissions(prev =>
                    prev.map(p => ({
                        ...p,
                        actions: p.actions.map(a => ({ ...a, isChecked: checked }))
                    }))
                );
            }
        });
    };

    const handleSave = () => {
        mutation.mutate({ roleId, permissions, roleName });
    };

    const handleSync = () => {
        Modal.confirm({
            title: 'Sync Actions',
            content: 'This will refresh all actions from backend. Are you sure you want to continue?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => mutationSync.mutate({})
        });
    };


    if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
    if (isError) return <div>Error fetching permissions.</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h1>Permissions for {roleName}</h1>
                <div>
                    <Switch
                        checked={isAllSelected()}
                        onChange={handleToggleAll}
                        checkedChildren="Select All"
                        unCheckedChildren="Unselect All"
                        style={{ marginRight: 8 }}
                    />
                    <Button
                        htmlType="button"
                        onClick={() => navigate({ to: '/management/roles' })}
                        style={{ marginRight: 8 }}
                    >
                        Go Back
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSync}
                        loading={mutationSync.isPending}
                        style={{ marginRight: 8 }}
                    >
                        Sync Actions
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={mutation.isPending}
                    >
                        Save
                    </Button>
                </div>
            </div>
            <Row gutter={[16, 16]}>
                {permissions.map((permission) => (
                    <Col span={8} key={permission.controllerName}>
                        <Card title={permission.controllerName} style={{ height: '100%' }}>
                            {permission.actions.map((action) => (
                                <div key={action.actionName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span>{action.actionName}</span>
                                    <Switch
                                        checked={action.isChecked}
                                        onChange={(checked) => handleSwitchChange(permission.controllerName, action.actionName, checked)}
                                    />
                                </div>
                            ))}
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
}

export default RolePermissions;